import { io, Socket } from 'socket.io-client';

class SocketService {
  private socket: Socket | null = null;
  private isConnected: boolean = false;
  private isConnecting: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;
  private lastStationSubscriptionKey: string | null = null;
  private lastPriceSubscriptionKey: string | null = null;

  // Get the server URL - use environment variable or default to current host
  private getServerUrl(): string {
    if (process.env.REACT_APP_SOCKET_URL) {
      return process.env.REACT_APP_SOCKET_URL;
    }
    // In development, use proxy. In production, use same origin
    if (process.env.NODE_ENV === 'development') {
      return 'http://localhost:5000';
    }
    return window.location.origin;
  }

  connect(): void {
    if (this.isConnecting) {
      return;
    }
    if (this.socket?.connected) {
      return;
    }

    const serverUrl = this.getServerUrl();
    console.log('🔌 Connecting to Socket.IO server:', serverUrl);

    this.isConnecting = true;
    this.socket = io(serverUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: this.reconnectDelay,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
      timeout: 20000,
    });

    this.socket.on('connect', () => {
      console.log('✅ Socket.IO connected:', this.socket?.id);
      this.isConnected = true;
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.onConnect?.();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ Socket.IO disconnected:', reason);
      this.isConnected = false;
      this.isConnecting = false;
      this.onDisconnect?.(reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket.IO connection error:', error);
      this.isConnecting = false;
      this.reconnectAttempts++;
      this.onError?.(error);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('🔄 Socket.IO reconnected after', attemptNumber, 'attempts');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.onReconnect?.(attemptNumber);
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('🔄 Socket.IO reconnection attempt', attemptNumber);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('❌ Socket.IO reconnection failed after', this.maxReconnectAttempts, 'attempts');
      this.onReconnectFailed?.();
    });
  }

  disconnect(): void {
    if (this.socket) {
      console.log('🔌 Disconnecting Socket.IO');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.isConnecting = false;
      this.lastStationSubscriptionKey = null;
      this.lastPriceSubscriptionKey = null;
    }
  }

  // Subscribe to station updates for a specific area
  subscribeToStations(bounds: { north: number; south: number; east: number; west: number } | null, center: { lat: number; lng: number }): void {
    if (!this.socket?.connected) {
      return;
    }
    const key = `${center.lat.toFixed(3)}|${center.lng.toFixed(3)}|${
      bounds ? `${bounds.north.toFixed(3)}|${bounds.south.toFixed(3)}|${bounds.east.toFixed(3)}|${bounds.west.toFixed(3)}` : 'no-bounds'
    }`;
    if (this.lastStationSubscriptionKey === key) {
      return;
    }

    this.unsubscribeFromStations();
    this.socket.emit('subscribe:stations', { bounds, center });
    this.lastStationSubscriptionKey = key;
  }

  // Unsubscribe from station updates
  unsubscribeFromStations(): void {
    if (!this.socket?.connected) {
      return;
    }

    console.log('📍 Unsubscribing from station updates');
    this.socket.emit('unsubscribe:stations');
    this.lastStationSubscriptionKey = null;
  }

  // Subscribe to price updates for specific stations
  subscribeToPrices(stationCodes: string[]): void {
    if (!this.socket?.connected) {
      return;
    }

    if (stationCodes.length === 0) {
      return;
    }

    const normalized = Array.from(new Set(stationCodes)).sort();
    const key = normalized.join(',');
    if (!key || this.lastPriceSubscriptionKey === key) {
      return;
    }

    this.unsubscribeFromPrices();
    this.socket.emit('subscribe:prices', { stationCodes });
    this.lastPriceSubscriptionKey = key;
  }

  // Unsubscribe from price updates
  unsubscribeFromPrices(): void {
    if (!this.socket?.connected) {
      return;
    }

    console.log('💰 Unsubscribing from price updates');
    this.socket.emit('unsubscribe:prices');
    this.lastPriceSubscriptionKey = null;
  }

  // Listen for station updates
  onStationUpdate(callback: (data: any) => void): void {
    if (!this.socket) {
      console.warn('⚠️ Cannot listen: Socket not initialized');
      return;
    }

    this.socket.on('station:update', callback);
  }

  // Listen for price updates
  onPriceUpdate(callback: (data: { stationCode: string; [key: string]: any }) => void): void {
    if (!this.socket) {
      console.warn('⚠️ Cannot listen: Socket not initialized');
      return;
    }

    this.socket.on('price:update', callback);
  }

  // Remove listeners
  offStationUpdate(callback?: (data: any) => void): void {
    if (this.socket) {
      this.socket.off('station:update', callback);
    }
  }

  offPriceUpdate(callback?: (data: any) => void): void {
    if (this.socket) {
      this.socket.off('price:update', callback);
    }
  }

  onFuelPricesInvalidate(callback: (data: { version: number; updatedAt: string; reason?: string }) => void): void {
    if (!this.socket) {
      console.warn('⚠️ Cannot listen: Socket not initialized');
      return;
    }
    this.socket.on('fuel-prices:invalidate', callback);
  }

  offFuelPricesInvalidate(
    callback?: (data: { version: number; updatedAt: string; reason?: string }) => void
  ): void {
    if (this.socket) {
      this.socket.off('fuel-prices:invalidate', callback);
    }
  }

  // Callbacks
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onError?: (error: Error) => void;
  onReconnect?: (attemptNumber: number) => void;
  onReconnectFailed?: () => void;

  // Getters
  get connected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }

  get socketId(): string | undefined {
    return this.socket?.id;
  }
}

// Export singleton instance
export const socketService = new SocketService();

